/**
 * MONSTER TOWN — THE MONSTER CREATOR. MONSTER CREW (src/monster/**).
 *
 * The first thing a kid ever does, and the thing they will redo a hundred times.
 *
 * RULES BAKED IN (not options):
 *  - NO WORDS. Every button is a picture of what will happen (LAW #4).
 *  - Every target is 88 CSS px with an ink border (kid-usability rule), and
 *    there is exactly ONE button treatment on the screen. Four different button
 *    looks at once (white squares, cream squares, floating camera pills, orange
 *    selections) told a 3-year-old that some of them were a different kind of
 *    thing, which none of them were.
 *  - It is a TAKEOVER, not an overlay: `stage.ts` hides the town, drops a plinth
 *    and a backdrop, turns the monster to face the camera and frames it. This
 *    screen is about *them*, and nothing else may be on it.
 *  - The preview is the REAL monster in the REAL world, not a separate model —
 *    a change is visible in the same frame, and there is no "apply" step.
 *  - There is no cancel and nothing to get wrong: every choice is instantly
 *    reversible by tapping another one, and the only exit is the big green tick,
 *    which is deliberately the biggest thing on the screen.
 *
 * WAYS IN (it is NOT one-shot — that was a shipped bug):
 *   1. first launch, when a slot has never had a monster made in it;
 *   2. the "my monster" button in the HUD's left stack, any time, for ever;
 *   3. `app.setPhase('monsterCreator')` or `ui:panel { panel: 'monsterCreator' }`
 *      from any crew — that is how a mirror, a wardrobe or a shop try-on will
 *      open it later without touching this file.
 * A kid who taps the tick in the first two seconds, or who wants a purple
 * monster tomorrow, can always come back and change everything.
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import { MONSTER_BODY_COLORS, type PaletteName } from '../core/palette'
import { world } from '../core/scene'
import type {
  MonsterBodyShape,
  MonsterConfig,
  MonsterEyes,
  MonsterHorns,
  MonsterPattern,
  Phase,
} from '../core/types'
import { app, type GameContext } from '../app/context'
import { HAT_IDS } from './wearables'
import {
  bigDoneIcon,
  catColorIcon,
  catEyesIcon,
  catHatIcon,
  catHornsIcon,
  catPatternIcon,
  catShapeIcon,
  colorIcon,
  diceIcon,
  eyesIcon,
  hatIcon,
  hornsIcon,
  iconCanvas,
  patternIcon,
  shapeIcon,
  turnIcon,
  type IconPainter,
} from './icons'
import {
  CREATOR_FRAME_HEIGHT,
  CREATOR_STAGE_HEIGHT,
  haltPlayer,
  markPlayerCreated,
  playerAction,
  playerLookCreated,
  playerPosition,
  playerRig,
  setCreatorReserve,
  setPlayerLook,
  setPlayerSpin,
  wardrobe,
} from './player'
import { closeStage, openStage, reframeStage } from './stage'
import './events'

const SHAPES: readonly MonsterBodyShape[] = ['blob', 'round', 'chunky', 'tall']
const EYES: readonly MonsterEyes[] = ['two', 'one', 'three', 'sleepy']
const HORNS: readonly MonsterHorns[] = ['nubs', 'curved', 'antlers', 'antennae', 'none']
const PATTERNS: readonly MonsterPattern[] = ['belly', 'spots', 'stripes', 'star', 'plain']
/** Accent choices offered alongside the body colour. */
const ACCENTS: readonly PaletteName[] = [
  'monsterBelly',
  'monsterCream',
  'flowerPink',
  'flowerYellow',
  'wallBlue',
  'monsterLilac',
]

interface Choice {
  key: string
  icon: IconPainter
  on: (cfg: MonsterConfig) => boolean
  apply: (cfg: MonsterConfig) => MonsterConfig
}

interface Category {
  id: string
  icon: IconPainter
  label: string
  choices: (cfg: MonsterConfig) => Choice[]
}

const CATEGORIES: readonly Category[] = [
  {
    id: 'shape',
    icon: catShapeIcon,
    label: 'Body shape',
    choices: (cfg) =>
      SHAPES.map((s) => ({
        key: s,
        icon: shapeIcon(s, cfg.color),
        on: (c) => c.shape === s,
        apply: (c) => ({ ...c, shape: s }),
      })),
  },
  {
    id: 'color',
    icon: catColorIcon,
    label: 'Colour',
    choices: () =>
      MONSTER_BODY_COLORS.map((col) => ({
        key: col,
        icon: colorIcon(col),
        on: (c) => c.color === col,
        apply: (c) => ({ ...c, color: col, accentColor: c.accentColor === col ? 'monsterBelly' : c.accentColor }),
      })),
  },
  {
    id: 'pattern',
    icon: catPatternIcon,
    label: 'Pattern',
    choices: (cfg) => {
      const out: Choice[] = PATTERNS.map((p) => ({
        key: `p:${p}`,
        icon: patternIcon(p, cfg.color, cfg.accentColor),
        on: (c) => c.pattern === p,
        apply: (c) => ({ ...c, pattern: p }),
      }))
      for (const a of ACCENTS) {
        out.push({
          key: `a:${a}`,
          icon: patternIcon(cfg.pattern === 'plain' ? 'spots' : cfg.pattern, cfg.color, a),
          on: (c) => c.accentColor === a,
          apply: (c) => ({ ...c, accentColor: a, pattern: c.pattern === 'plain' ? 'spots' : c.pattern }),
        })
      }
      return out
    },
  },
  {
    id: 'eyes',
    icon: catEyesIcon,
    label: 'Eyes',
    choices: () =>
      EYES.map((e) => ({
        key: e,
        icon: eyesIcon(e),
        on: (c) => c.eyes === e,
        apply: (c) => ({ ...c, eyes: e }),
      })),
  },
  {
    id: 'horns',
    icon: catHornsIcon,
    label: 'Horns',
    choices: () =>
      HORNS.map((h) => ({
        key: h,
        icon: hornsIcon(h),
        on: (c) => c.horns === h,
        apply: (c) => ({ ...c, horns: h }),
      })),
  },
  {
    id: 'hat',
    icon: catHatIcon,
    label: 'Hat',
    choices: () => {
      const owned = wardrobe()
      const list: Choice[] = [
        { key: 'none', icon: hatIcon(null), on: (c) => c.hat === null, apply: (c) => ({ ...c, hat: null }) },
      ]
      for (const id of HAT_IDS) {
        if (!owned.includes(id)) continue
        list.push({
          key: id,
          icon: hatIcon(id),
          on: (c) => c.hat === id,
          apply: (c) => ({ ...c, hat: id }),
        })
      }
      return list
    },
  },
]

// ---------------------------------------------------------------------------
// dom
// ---------------------------------------------------------------------------

let ctxRef: GameContext | null = null
let rootEl: HTMLDivElement | null = null
let catRowEl: HTMLDivElement | null = null
let optRowEl: HTMLDivElement | null = null
let activeCat = 'color'
let open = false
/** Where to put the kid back when the tick is tapped. */
let restorePhase: Phase = 'town'
let restoreBuildMode = false
/** Quarter turns the kid has spun their monster on the plinth. */
let spin = 0

const CSS = `
#mt-creator {
  position: absolute;
  left: 0; right: 0; bottom: 0;
  padding: 0 calc(var(--safe-r) + 12px) calc(var(--safe-b) + 14px) calc(var(--safe-l) + 12px);
  display: flex; justify-content: center;
  pointer-events: none;
}
/*
 * ONE island, centred, sized to its contents. The old sheets were two
 * full-width bars that were about 45% empty cream with the dice and the tick
 * marooned at the far right edge — which on an iPad is the corner a small child
 * reaches for last.
 */
#mt-creator .mt-cr-island {
  pointer-events: auto;
  max-width: 100%;
  background: var(--ui-bg);
  border: 4px solid var(--ink);
  border-radius: 34px;
  box-shadow: var(--panel-shadow, 0 10px 0 rgba(43,36,56,0.16));
  padding: 12px 14px;
  display: flex; flex-direction: column; gap: 10px;
}
#mt-creator .mt-cr-line {
  display: flex; gap: 12px; align-items: center;
}
/*
 * THE SCROLLING PART AND THE ALWAYS-THERE PART ARE DIFFERENT BOXES.
 *
 * With one scrolling row per line, a portrait iPad pushed the green tick off the
 * right-hand edge — the one button that ends the screen was reachable only by
 * swiping first. Choices scroll; turn, dice and done never move.
 */
#mt-creator .mt-cr-row {
  display: flex;
  flex: 1 1 auto; min-width: 0;
  overflow-x: auto; overflow-y: hidden;
  touch-action: pan-x;
  scrollbar-width: none;
}
#mt-creator .mt-cr-row::-webkit-scrollbar { display: none; }
/* auto margins centre the strip when it fits and collapse when it does not,
   so nothing is ever clipped off the START of a scrolling row either. */
#mt-creator .mt-cr-strip {
  display: flex; gap: 12px; align-items: center;
  margin: 0 auto; padding: 3px;
}
#mt-creator .mt-cr-actions {
  display: flex; gap: 12px; align-items: center;
  flex: 0 0 auto; padding: 3px;
}
#mt-creator .mt-cr-btn { flex: 0 0 auto; }
#mt-creator .mt-cr-btn {
  pointer-events: auto;
  width: var(--touch-min); height: var(--touch-min);
  border: 4px solid var(--ink);
  border-radius: 26px;
  background: var(--ui-panel);
  box-shadow: 0 var(--lift, 6px) 0 rgba(43,36,56,0.22);
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0; cursor: pointer;
  transition: transform 90ms ease, box-shadow 90ms ease, background 120ms ease;
}
#mt-creator .mt-cr-btn:active,
#mt-creator .mt-cr-btn[data-press='1'] {
  transform: translateY(var(--lift, 6px));
  box-shadow: 0 0 0 rgba(43,36,56,0.22);
}
#mt-creator .mt-cr-btn[data-on='true'] { background: var(--ui-primary); }
/* The one control that ends the screen. Bigger, green, and last in reading order
   however the row is laid out — priority by size and colour, not by position. */
#mt-creator .mt-cr-done {
  width: 116px; height: 116px;
  border-radius: 34px;
  background: var(--ui-ok);
}
#mt-creator .mt-cr-sep {
  width: 4px; height: 64px; flex: 0 0 auto;
  background: var(--ui-border, #2B2438);
  opacity: 0.16; border-radius: 3px;
  margin: 0 2px;
}
@media (prefers-reduced-motion: reduce) {
  #mt-creator .mt-cr-btn { transition: none; }
}
/*
 * While the sheet is open, every control that would take a kid SOMEWHERE ELSE
 * goes away. This is a no-dead-ends rule, not a layout tidy-up: "Which town"
 * used to stay live during creation, and tapping it opened the slot picker over
 * the half-made monster — one more tap committed a monster that was never
 * finished and loaded the other kid's town with the creator still on screen.
 *
 * .mt-topright in the original rule matched nothing (the camera cluster is
 * .mt-cam) and .mt-leftstack was never listed, which is how that hole survived.
 *
 * THE CAMERA CLUSTER NOW GOES TOO. Turning your monster round is still the
 * point, but it is done with the turn button IN the island — same size, same
 * border, same shadow as everything else on the screen — instead of a fifth
 * floating widget hovering over the stage in the corner.
 */
#hud[data-mt-creator='true'] .mt-purse,
#hud[data-mt-creator='true'] .mt-leftstack,
#hud[data-mt-creator='true'] .mt-cam,
#hud[data-mt-creator='true'] .mt-topright,
#hud[data-mt-creator='true'] .mt-botright,
#hud[data-mt-creator='true'] .mt-tickets,
#hud[data-mt-creator='true'] .mt-nudge,
#hud[data-mt-creator='true'] .mt-tray,
#hud[data-mt-creator='true'] .mt-rotate,
#hud[data-mt-creator='true'] #mt-build,
#hud[data-mt-creator='true'] .mt-botleft { display: none !important; }
`

function ensureStyle(): void {
  if (document.getElementById('mt-monster-css') !== null) return
  const style = document.createElement('style')
  style.id = 'mt-monster-css'
  style.textContent = CSS
  document.head.appendChild(style)
}

function iconButton(
  paint: IconPainter,
  label: string,
  onTap: () => void,
  opts: { className?: string; size?: number } = {},
): HTMLButtonElement {
  const b = document.createElement('button')
  b.className = `mt-cr-btn ${opts.className ?? ''}`.trim()
  b.type = 'button'
  b.setAttribute('aria-label', label)
  b.title = label
  b.appendChild(iconCanvas(paint, opts.size ?? 70))
  // Press is committed on pointerup over the same control, like every other
  // button in the game: a finger that slides off has changed its mind.
  b.addEventListener('pointerdown', (e) => {
    e.stopPropagation()
    b.dataset['press'] = '1'
  })
  const release = (): void => {
    delete b.dataset['press']
  }
  b.addEventListener('pointerleave', release)
  b.addEventListener('pointercancel', release)
  b.addEventListener('pointerup', (e) => {
    e.preventDefault()
    e.stopPropagation()
    const armed = b.dataset['press'] === '1'
    release()
    if (armed) onTap()
  })
  return b
}

function buildDom(): void {
  ensureStyle()
  if (ctxRef === null) return
  const root = document.createElement('div')
  root.id = 'mt-creator'

  const island = document.createElement('div')
  island.className = 'mt-cr-island'

  // line 1: the choices for the category you are in
  const optLine = document.createElement('div')
  optLine.className = 'mt-cr-line'
  const optScroll = document.createElement('div')
  optScroll.className = 'mt-cr-row'
  const optRow = document.createElement('div')
  optRow.className = 'mt-cr-strip mt-cr-opts'
  optScroll.appendChild(optRow)
  optLine.appendChild(optScroll)
  island.appendChild(optLine)

  // line 2: the categories, then the three controls that never scroll away
  const catLine = document.createElement('div')
  catLine.className = 'mt-cr-line'
  const catScroll = document.createElement('div')
  catScroll.className = 'mt-cr-row'
  const catRow = document.createElement('div')
  catRow.className = 'mt-cr-strip mt-cr-cats'
  catScroll.appendChild(catRow)
  catLine.appendChild(catScroll)

  const actions = document.createElement('div')
  actions.className = 'mt-cr-actions'
  actions.appendChild(separator())
  actions.appendChild(
    iconButton(turnIcon, 'Turn my monster round', () => {
      turn()
    }),
  )
  actions.appendChild(
    iconButton(diceIcon, 'Surprise me', () => {
      randomise()
    }),
  )
  actions.appendChild(
    iconButton(
      bigDoneIcon,
      'All done',
      () => {
        finish()
      },
      { className: 'mt-cr-done', size: 92 },
    ),
  )
  catLine.appendChild(actions)
  island.appendChild(catLine)

  root.appendChild(island)
  ctxRef.hud.appendChild(root)

  rootEl = root
  catRowEl = catRow
  optRowEl = optRow
  paintCategories()
  paintOptions()
}

function separator(): HTMLElement {
  const s = document.createElement('span')
  s.className = 'mt-cr-sep'
  return s
}

function paintCategories(): void {
  if (catRowEl === null) return
  catRowEl.replaceChildren()
  for (const cat of CATEGORIES) {
    if (cat.id === 'hat' && wardrobe().length === 0) continue
    const b = iconButton(cat.icon, cat.label, () => {
      activeCat = cat.id
      paintCategories()
      paintOptions()
      bus.emit('audio:cue', { cue: 'ui_tap' })
    })
    b.dataset['on'] = String(cat.id === activeCat)
    catRowEl.appendChild(b)
  }
}

function paintOptions(): void {
  if (optRowEl === null) return
  const cat = CATEGORIES.find((c) => c.id === activeCat) ?? CATEGORIES[0]
  if (cat === undefined) return
  optRowEl.replaceChildren()
  for (const choice of cat.choices(app.monster)) {
    const b = iconButton(choice.icon, `${cat.label} ${choice.key}`, () => choose(choice))
    b.dataset['on'] = String(choice.on(app.monster))
    optRowEl.appendChild(b)
  }
  optRowEl.scrollLeft = 0
}

function choose(choice: Choice): void {
  const next = choice.apply(app.monster)
  setPlayerLook(next, 'creatorChoice')
  bus.emit('monster:lookChanged', { look: next })
  bus.emit('audio:cue', { cue: 'pop_soft' })
  playerAction('hop')
  paintOptions()
  paintCategories()
}

/** Spin the monster on its plinth so a kid can see its back and its horns. */
function turn(): void {
  spin += Math.PI / 2
  setPlayerSpin(spin)
  bus.emit('audio:cue', { cue: 'rotate' })
}

function randomise(): void {
  const r = engine.rng
  const pickOne = <T>(list: readonly T[], fallback: T): T => list[Math.floor(r() * list.length)] ?? fallback
  const owned = wardrobe()
  const next: MonsterConfig = {
    shape: pickOne(SHAPES, 'blob'),
    color: pickOne(MONSTER_BODY_COLORS, 'monsterTeal'),
    accentColor: pickOne(ACCENTS, 'monsterBelly'),
    eyes: pickOne(EYES, 'two'),
    horns: pickOne(HORNS, 'nubs'),
    pattern: pickOne(PATTERNS, 'belly'),
    hat: owned.length > 0 && r() < 0.6 ? (owned[Math.floor(r() * owned.length)] ?? null) : null,
    outfit: app.monster.outfit,
  }
  setPlayerLook(next, 'creatorRandom')
  bus.emit('monster:lookChanged', { look: next })
  bus.emit('audio:cue', { cue: 'sparkle_soft' })
  playerAction('spin')
  paintOptions()
  paintCategories()
}

function finish(): void {
  const first = !playerLookCreated()
  markPlayerCreated()
  if (first) bus.emit('monster:created', { look: app.monster })
  bus.emit('audio:cue', { cue: 'chime_happy' })
  playerAction('cheer')
  closeCreator()
}

// ---------------------------------------------------------------------------
// open / close
// ---------------------------------------------------------------------------

export function creatorIsOpen(): boolean {
  return open
}

/** Where a kid belongs when the creator closes: back in whatever room they are in. */
function phaseForActiveScene(): Phase {
  const id = world.activeSceneId
  if (id === null) return 'town'
  return world.get(id)?.def.kind === 'interior' ? 'houseInterior' : 'town'
}

export function openCreator(): void {
  if (open || ctxRef === null) return
  open = true
  haltPlayer()

  // Remember where this kid came from, so the tick puts them back there rather
  // than teleporting them to the town from inside their own kitchen.
  restorePhase = app.phase === 'monsterCreator' ? phaseForActiveScene() : app.phase
  restoreBuildMode = app.mode === 'build'
  if (restoreBuildMode) app.setMode('play')

  spin = 0
  setPlayerSpin(spin)

  /**
   * THE DOCK IS BUILT BEFORE THE CAMERA IS AIMED, because the camera needs to
   * know how big it is.
   *
   * The stage used to be opened first and framed the monster into the WHOLE
   * screen, so the option island — a third of an iPad — landed on top of the
   * bottom third of the monster. The critic's read was exact: the hands cut off
   * and everything below the chest hidden, on the screen where the second row of
   * buttons is BODY SHAPE. You cannot choose a shape you cannot see.
   */
  ctxRef.hud.dataset['mtCreator'] = 'true'
  if (rootEl === null) buildDom()
  else {
    ctxRef.hud.appendChild(rootEl)
    paintCategories()
    paintOptions()
  }
  measureDock()
  // The last argument is the takeover's guest list: the kid's monster, nobody
  // else. Without it a neighbour who happened to be walking past stayed on the
  // stage behind them and read as part of their body.
  openStage(
    ctxRef.camera,
    playerPosition(),
    CREATOR_STAGE_HEIGHT,
    dockReserve,
    CREATOR_FRAME_HEIGHT,
    () => playerRig()?.root ?? null,
  )
  // An iPad turned over mid-creation must re-frame, not bury the monster.
  window.addEventListener('resize', onResize)
  window.addEventListener('orientationchange', onResize)

  if (app.phase !== 'monsterCreator') app.setPhase('monsterCreator')
  playerAction('wave')
}

/**
 * What fraction of the CANVAS the option island covers, measured off the real
 * element. Canvas bounding rect, never `window.innerHeight` — the hard rule,
 * and it matters here because an older iPad reports a window height that does
 * not match the canvas, which would mis-frame the very first screen of the game.
 */
let dockReserve = 0

function measureDock(): void {
  if (ctxRef === null) return
  const canvas = ctxRef.canvas.getBoundingClientRect()
  const island = rootEl?.querySelector('.mt-cr-island')?.getBoundingClientRect() ?? null
  if (island === null || canvas.height <= 0) {
    dockReserve = 0
    setCreatorReserve(0)
    return
  }
  // From the top of the island to the bottom of the canvas: the dock plus the
  // safe-area padding under it are equally unavailable to the monster.
  const covered = Math.max(0, canvas.bottom - island.top)
  // A small breathing gap so the monster's feet never touch the island's border.
  dockReserve = Math.max(0, Math.min(0.55, covered / canvas.height))
  setCreatorReserve(dockReserve)
}

function onResize(): void {
  if (!open || ctxRef === null) return
  measureDock()
  // `openStage` is idempotent, so re-framing is its own call.
  reframeStage(ctxRef.camera, CREATOR_FRAME_HEIGHT, dockReserve)
}

export function closeCreator(): void {
  if (!open || ctxRef === null) return
  open = false
  if (rootEl !== null) rootEl.remove()
  delete ctxRef.hud.dataset['mtCreator']
  window.removeEventListener('resize', onResize)
  window.removeEventListener('orientationchange', onResize)
  setCreatorReserve(0)
  setPlayerSpin(null)
  closeStage(ctxRef.camera)
  if (restoreBuildMode) app.setMode('build')
  if (app.phase === 'monsterCreator') app.setPhase(restorePhase)
}

export function installCreator(ctx: GameContext): void {
  ctxRef = ctx

  // The phase is the contract: anyone (HUD, shop, harness) can put the game in
  // the creator by setting it, and taking it out closes the sheet.
  bus.on('app:phaseChanged', (e) => {
    if (e.phase === 'monsterCreator') openCreator()
    else if (open) closeCreator()
  })

  // A picture panel request from any crew's button — the HUD's "my monster"
  // control, and any mirror or wardrobe added later.
  bus.on('ui:panel', (e) => {
    if (e.panel === 'monsterCreator') openCreator()
  })

  // A scene change under an open creator would leave the stage attached to the
  // scene the kid just left, and the town hidden in it for ever.
  bus.on('scene:transitionStart', () => {
    if (open) closeCreator()
  })
}

/**
 * OPT IN TO THE FIRST RUN UNDER AUTOMATION.
 *
 * Skipping the creator for automated browsers keeps `shot`/`perf`/`drive` off a
 * takeover screen they never asked for. The cost was that NO tool could look at
 * the first thing a kid ever sees, and a critic round duly reported "on turn one
 * there is nothing of the kid's on screen" from a capture that had skipped turn
 * one entirely. `window.__harness.firstRun(slot)` flips this, so the real
 * first-launch flow is reviewable without any gate changing behaviour.
 */
let firstRunUnderAutomation = false

export function allowFirstRunUnderAutomation(on = true): void {
  firstRunUnderAutomation = on
}

/**
 * Open the creator the first time a kid ever plays a slot. Runs on every
 * `save:loaded`, so switching to a slot that has never been played — the second
 * kid picking up the same iPad — gets the same first act of ownership as a first
 * launch does.
 */
export function maybeOpenOnFirstLaunch(): void {
  if (playerLookCreated()) return
  const automated = typeof navigator !== 'undefined' && navigator.webdriver === true
  if (automated && !firstRunUnderAutomation) return
  openCreator()
}
